'use client';

import { useState, useRef } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';

import { Icon } from '@/components/ui/icon.component';
import useIsMobile from '@/hooks/use-is-mobile.hook';

interface FilterPillProps {
  id: string;
  label: string;
  icon: IconDefinition & IconProp;
  action: () => void;
  variant: 'success' | 'light';
  className?: string;
  dataTestId?: string;
}

const getVariantClasses = (variant: 'success' | 'light') => {
  switch (variant) {
    case 'success':
      return 'bg-success-100 text-success-700 border-success-400';
    case 'light':
      return 'bg-gray-100 text-gray-700 border-gray-400';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-400';
  }
};

export const FilterPill = ({
  id,
  label,
  icon,
  action,
  variant,
  className,
  dataTestId,
}: FilterPillProps) => {
  const isMobile = useIsMobile();
  const [isDragging, setIsDragging] = useState(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const select = () => {
    action();
    if (isMobile) {
      const currentElement = document.getElementById(id);

      if (currentElement) {
        const container = currentElement.closest('.overflow-x-auto');

        if (container) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = currentElement.getBoundingClientRect();

          const left
            = elementRect.left
              - containerRect.left
              + container.scrollLeft;

          container.scrollTo({
            left: left - containerRect.width / 2 + elementRect.width / 2,
            behavior: 'smooth',
          });
        }
      }
    }
  };

  const handleClick = () => {
    if (!isDragging) {
      select();
    }
    setIsDragging(false);
    startPosRef.current = null;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') {
      return;
    }
    e.preventDefault();
    if (!e.repeat) {
      select();
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    startPosRef.current = { x: e.clientX, y: e.clientY };
    setIsDragging(false);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (startPosRef.current) {
      const deltaX = Math.abs(e.clientX - startPosRef.current.x);
      const deltaY = Math.abs(e.clientY - startPosRef.current.y);

      if (deltaX > 10 || deltaY > 10) {
        setIsDragging(true);
      }
    }
  };

  return (
    <div
      id={id}
      role="button"
      tabIndex={0}
      aria-pressed={variant === 'success'}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handleClick}
      onKeyDown={handleKeyDown}
      className={
        `flex-shrink-0
         px-2 py-1 rounded-md border w-fit flex items-center gap-1 text-base font-semibold cursor-pointer scroll-smooth
         outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green-500
         ${getVariantClasses(variant)}
         ${className || ''}`
      }
      data-testid={dataTestId}
    >
      <Icon data-testid="pill-start-icon" icon={icon} size="sm" className="mt-[1px]" />
      <div className="flex justify-content truncate">
        <span data-testid="pill-label" className="truncate">
          {label}
        </span>
      </div>
    </div>
  );
};
